import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import Select, { components } from "react-select";
import { useLocation, useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { URLS } from "../../Services/Constant";
import { post, get, put } from "../../Services/ApiHandler";
import { toast } from "react-toastify";
import moment from "moment";

const hideMultiValue = {
  multiValue: (provided) => ({ ...provided }),
  valueContainer: (provided) => ({
    ...provided,
    height: "55px",
    padding: "0 6px",
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "55px",
    borderRadius: "8px",
    backgroundColor: "#F4F4F4",
    border: "none",
    boxShadow: "none",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const CheckboxOption = (props) => {
  const isAllUsersActive = props.selectProps.isAllUsersSelected;
  const excludedIds = props.selectProps.excludedUserIds || [];
  const isAllGenericActive = props.selectProps.isAllSelected;

  let isChecked = props.isSelected;

  if (isAllUsersActive) {
    if (props.value === "all") {
      isChecked = excludedIds.length === 0;
    } else {
      isChecked = !excludedIds.some(
        (exId) => String(exId) === String(props.value),
      );
    }
  } else if (isAllGenericActive) {
    isChecked = true;
  }

  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => null}
          style={{
            marginRight: "10px",
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "#A140E0",
          }}
        />
        <span>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CustomMenuList = (props) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      {props.selectProps.isLoadingUsers && (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            color: "#A140E0",
            fontSize: "14px",
            fontWeight: "bold",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          Loading more users...
        </div>
      )}
    </components.MenuList>
  );
};

const SendTemplateNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { template, type, data } = location.state || {};
  const isViewMode = type === "view";
  const isEditMode = type === "edit";
  const ID = data?.id;

  const { control, handleSubmit, setValue, getValues, watch, formState: { errors } } = useForm({
    defaultValues: {
      templateName: template?.name || data?.template_name || data?.title || "",
      state: [],
      city: [],
      status: null,
      service: null,
      userCount: 0,
      userList: [],
      fromDate: data?.from_date || moment().subtract(1, 'months').format("YYYY-MM-DD"),
      toDate: data?.to_date || moment().format("YYYY-MM-DD"),
      title: "",
      description: "",
      sendDate: "",
      sendTime: "",
      selectAllCity: false,
      selectAllStatus: false,
      selectAllService: false,
      template_id: data?.template_id || template?.id || null,
    }
  });

  const [locationList, setLocationsList] = useState({ states: [], cities: [] });
  const [isInitializing, setIsInitializing] = useState(!!ID);

  const [selectedUserOptions, setSelectedUserOptions] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAllUsersSelected, setIsAllUsersSelected] = useState(false);
  const [excludedUserIds, setExcludedUserIds] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [allUserIds, setAllUserIds] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);

  const debounceTimer = useRef(null);
  const isFetchingRef = useRef(false);

  const fromDate = watch("fromDate");
  const toDate = watch("toDate");

  // Pre-fill state and city based on template
  useEffect(() => {
    if (template?.id) {
      if (template.state_names && template.state_ids) {
        const prefilledStates = template.state_ids.map((id, index) => ({
          value: id, label: template.state_names[index] || id
        }));
        setValue("state", prefilledStates);
      }
      if (template.city_names && template.city_ids) {
        const prefilledCities = template.city_ids.map((id, index) => ({
          value: id, label: template.city_names[index] || id
        }));
        setValue("city", prefilledCities);
      }
      
      // Status (notification_type)
      const notificationLabels = { 1: "Birth Date", 2: "Booked", 3: "Un Booked", 4: "Cancellation" };
      if (template.notification_type === 0 || !template.notification_type) {
        setValue("status", [{ value: "all", label: "All" }]);
      } else {
        setValue("status", [{ value: template.notification_type, label: notificationLabels[template.notification_type] || "Selected" }]);
      }

      // Service type
      if (template.service === 0 || !template.service) {
        setValue("service", [{ value: "all", label: "All" }]);
      } else {
        setValue("service", [{ value: template.service, label: `Service ${template.service}` }]);
      }
    }
  }, [template, setValue]);

  const fetchUserList = useCallback(
    async (pageUrl = null, searchString = "") => {
      if (isInitializing && ID) return;
      if (isFetchingRef.current) return;

      const requiresDates = template?.notification_type > 0;
      if (requiresDates && (!fromDate || !toDate)) {
        setSelectedUserOptions([]);
        return;
      }

      const payload = {
        state_ids: template?.state_ids || [],
        city_ids: template?.city_ids || [],
        area_ids: [],
        customer_type: template?.notification_type || 0,
        from_date: fromDate || null,
        to_date: toDate || null,
        search: searchString,
      };

      if (template?.service && template.service !== "all" && template.service !== 0) {
        payload.service_id = template.service;
      }

      if (
        payload.state_ids.length === 0 &&
        payload.city_ids.length === 0 &&
        payload.customer_type === 0 &&
        !payload.from_date &&
        !payload.to_date
      ) {
        setSelectedUserOptions([]);
        return;
      }

      try {
        isFetchingRef.current = true;
        setIsLoadingUsers(true);

        let endpoint = URLS.USERNOTIFICATION_LIST;
        if (pageUrl) {
          endpoint = pageUrl.replace(/^.*\/\/[^\/]+/, "");
        }

        const res = await post(endpoint, payload);

        if (res?.status === "success" && Array.isArray(res.data)) {
          const isSearching = searchString && searchString.trim().length > 0;

          if (!isSearching) {
            setValue("userCount", res.count || 0);
            setTotalUserCount(res.count || 0);
            setAllUserIds(res.all_user_ids || []);
          }

          setNextPageUrl(res.next);

          const result = res.data.map((item) => ({
            label: `${item.name} (${item.code || "N/A"})`,
            value: item.id,
          }));

          if (pageUrl) {
            setSelectedUserOptions((prev) => [...prev, ...result]);
          } else {
            setSelectedUserOptions(result);

            const currentUsers = getValues("userList");
            if (!isSearching && (!currentUsers || currentUsers.length === 0)) {
              if (res.count && res.count > 0) {
                setIsAllUsersSelected(true);
                setExcludedUserIds([]);
                setValue("userList", [
                  { value: "all", label: `All (${res.count})` },
                ]);
              } else {
                setIsAllUsersSelected(false);
                setExcludedUserIds([]);
                setValue("userList", []);
              }
            }
          }
        } else {
          if (!pageUrl) setSelectedUserOptions([]);

          const isSearching = searchString && searchString.trim().length > 0;
          if (!isSearching) {
            setValue("userCount", 0);
            setTotalUserCount(0);
            setAllUserIds([]);
          }
        }
      } catch (error) {
        console.error("Error fetching filtered users:", error);
        toast.error(error || "Failed to fetch users");
      } finally {
        isFetchingRef.current = false;
        setIsLoadingUsers(false);
      }
    },
    [template, fromDate, toDate, setValue, getValues, ID, isInitializing]
  );

  const handleUserSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      setSearchTerm(inputValue);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchUserList(null, inputValue);
      }, 500);
    }
  };

  const getLocationsList = useCallback(async (locationType, parent_id) => {
    try {
      const url = `${URLS.LOCATION}?type=${locationType}${parent_id ? "&parent_id=" + parent_id : ""}`;
      const res = await get(url);
      if (res?.status === "success") {
        const result = res.data.map((item) => ({ value: item.id, label: item.name }));
        if (locationType === 2) setLocationsList((prev) => ({ ...prev, states: result }));
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    getLocationsList(2);
  }, [getLocationsList]);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  useEffect(() => {
    if (ID && locationList.states.length > 0) {
      const getData = async () => {
        try {
          const res = await get(`${URLS.USER_TEMPLATE_SEND_NOTIFICATION_LIST}${ID}/`);
          if (res?.status === "success") {
            const viewData = res.data;

            setValue("templateName", data?.template_name || viewData.template_name || viewData.title || "");
            setValue("fromDate", viewData.from_date ? moment(viewData.from_date).format("YYYY-MM-DD") : "");
            setValue("toDate", viewData.to_date ? moment(viewData.to_date).format("YYYY-MM-DD") : "");
            setValue("title", viewData.title);
            setValue("description", viewData.description);
            setValue("sendDate", viewData.notification_date);
            setValue("sendTime", viewData.notification_time);
            setValue("template_id", viewData.template_id);
            
            const isAllStatesSelected = locationList.states.length > 0 && viewData.states?.length === locationList.states.length;
            
            if (isAllStatesSelected || !viewData.states || viewData.states.length === 0) {
              setValue("state", [{ value: "all", label: "All" }]);
              
              const cityRes = await get(`${URLS.LOCATION}?type=3`);
              if (cityRes?.status === "success") {
                const allCities = cityRes.data.map((item) => ({ value: item.id, label: item.name }));
                if (viewData.cities?.length === allCities.length || !viewData.cities || viewData.cities.length === 0) {
                  setValue("city", [{ value: "all", label: "All" }]);
                } else {
                  setValue("city", allCities.filter(c => viewData.cities.includes(c.value)));
                }
              }
            } else {
              const mappedStates = locationList.states.filter(s => viewData.states.includes(s.value));
              setValue("state", mappedStates);

              let allCities = [];
              for (const stateId of viewData.states) {
                const cityRes = await get(`${URLS.LOCATION}?type=3&parent_id=${stateId}`);
                if (cityRes?.status === "success") {
                  allCities = [...allCities, ...cityRes.data.map(i => ({ value: i.id, label: i.name }))];
                }
              }
              
              if (!viewData.cities || viewData.cities.length === 0) {
                setValue("city", [{ value: "all", label: "All" }]);
              } else {
                setValue("city", allCities.filter(c => viewData.cities.includes(c.value)));
              }
            }

            const notificationLabels = { 1: "Birth Date", 2: "Booked", 3: "Un Booked", 4: "Cancellation" };
            if (viewData.notification_type === 0 || !viewData.notification_type) {
              setValue("status", [{ value: "all", label: "All" }]);
            } else {
              setValue("status", [{ value: viewData.notification_type, label: notificationLabels[viewData.notification_type] || "Selected" }]);
            }

            if (viewData.service) {
              setValue("service", [{ value: viewData.service.id, label: viewData.service.display_name }]);
            } else {
              setValue("service", [{ value: "all", label: "All" }]);
            }

            const userFilterPayload = {
              state_ids: viewData.states || [],
              city_ids: viewData.cities || [],
              area_ids: [],
              customer_type: viewData.notification_type || 0,
              from_date: viewData.from_date || null,
              to_date: viewData.to_date || null,
              search: "",
            };
            if (viewData.service?.id) {
              userFilterPayload.service_id = viewData.service.id;
            }
            const userRes = await post(URLS.USERNOTIFICATION_LIST, userFilterPayload);
            if (userRes?.status === "success") {
              const totalCount = userRes.count || 0;
              const backendAllIds = userRes.all_user_ids || [];

              setTotalUserCount(totalCount);
              setValue("userCount", totalCount);
              setAllUserIds(backendAllIds);
              setNextPageUrl(userRes.next);

              const paginatedOptions = (userRes.data || []).map((u) => ({
                value: u.id,
                label: `${u.name} (${u.code || "N/A"})`,
              }));
              setSelectedUserOptions(paginatedOptions);

              const savedUserIds = (viewData.users || []).map((u) => String(u?.id || u));

              if (savedUserIds.length > 0) {
                const exclusions = backendAllIds.filter((id) => !savedUserIds.includes(String(id)));
                setIsAllUsersSelected(true);
                setExcludedUserIds(exclusions);

                if (savedUserIds.length === totalCount) {
                  setValue("userList", [{ value: "all", label: `All (${totalCount})` }]);
                } else {
                  setValue("userList", [{ value: "all", label: `Selected (${savedUserIds.length})` }]);
                }
              } else {
                setValue("userList", []);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching notification data:", error);
        } finally {
          setIsInitializing(false);
        }
      };
      getData();
    }
  }, [ID, data, setValue, locationList.states]);

  const onSubmit = async (formData) => {
    if (isViewMode) return;
    const getStateIds = () => {
      if (!formData?.state?.length) return [];
      if (formData.state[0]?.value === "all") {
        return locationList?.states?.map((s) => s.value) || [];
      }
      return formData.state.map((s) => s.value);
    };

    const getCityIds = () => {
      if (!formData?.city?.length) return [];
      if (formData.city[0]?.value === "all") {
        return locationList?.cities?.map((c) => c.value) || [];
      }
      return formData.city.map((c) => c.value);
    };

    const getFinalUserIds = () => {
      if (isAllUsersSelected) {
        const stringExclusions = excludedUserIds.map((id) => String(id));
        return allUserIds.filter(
          (id) => !stringExclusions.includes(String(id)),
        );
      }
      if (!formData?.userList) return [];
      return Array.isArray(formData.userList)
        ? formData.userList.map((item) => item?.value).filter(Boolean)
        : formData.userList?.value
          ? [formData.userList.value]
          : [];
    };

    try {
      const payload = {
        template_id: formData.template_id,
        notification_type: formData?.status?.[0]?.value === "all" ? 0 : formData?.status?.[0]?.value ?? 0,
        state_ids: getStateIds?.() ?? [],
        city_ids: getCityIds?.() ?? [],
        user_ids: getFinalUserIds(),
        from_date: formData?.fromDate ?? null,
        to_date: formData?.toDate ?? null,
        description: formData?.description ?? "",
        title: formData?.title ?? "",
        service_id: formData?.service?.[0]?.value && formData.service[0].value !== "all" ? formData.service[0].value : 0,
        all_users: isAllUsersSelected,
        selected_users: formData?.status?.[0]?.value !== "all" && formData?.status?.[0]?.value !== 0 ? true : false,
        notification_date: formData.sendDate ? moment(formData.sendDate).format("YYYY-MM-DD") : null,
        notification_time: formData.sendTime ? (formData.sendTime.length === 5 ? `${formData.sendTime}:00` : formData.sendTime) : null,
      };

      const url = isEditMode ? `${URLS.USER_SEND_NOTIFICATION_CREATE}${ID}/` : URLS.USER_SEND_NOTIFICATION_CREATE;
      const method = isEditMode ? put : post;

      const res = await method(url, payload);
      if (res?.status === "success") {
        toast.success(res.message || (isEditMode ? "Notification updated successfully!" : "Notification sent successfully!"));
        navigate(-1);
      } else {
        toast.error(res?.message || (isEditMode ? "Failed to update notification." : "Failed to send notification."));
      }
    } catch (error) {
      console.error("Error saving notification:", error);
      toast.error("Something went wrong!");
    }
  };

  const isServiceAllSelected = template?.notification_type === 0 || !template?.notification_type;

  return (
    <div className="layout_body p-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex align-items-center justify-content-between header mb-3">
        <h2 className="mt-0 mb-0 ms-2 fw-bold" style={{ fontSize: "24px", color: "#000" }}>
          Notification User Template
        </h2>
        <Topbar />
      </div>

      <div className="card-box mt-3 bg-white p-4 mx-2" style={{ borderRadius: "16px" }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          
          <div className="mb-4">
            <label className="f2 fs-14 mb-2 d-block fw-bold text-muted">
              Notification Template Name
            </label>
            <div className="w-100 d-block">
              <Controller
                name="templateName"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Template Name"
                    className="form-control w-100"
                    style={{
                      backgroundColor: "#F4F4F4",
                      borderRadius: "8px",
                      height: "55px",
                      width: "100%",
                      minWidth: "100%",
                      padding: "0 16px",
                      border: "none",
                      outline: "none",
                      color: "#999",
                      fontWeight: "500",
                      display: "block"
                    }}
                    readOnly
                  />
                )}
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-14 m-0 fw-bold text-muted">State</label>
                <div className="d-flex align-items-center">
                  <input type="checkbox" className="me-2" style={{ width: "16px", height: "16px", accentColor: "#b863f8" }} disabled />
                  <span className="fs-13 fw-bold text-muted">Select All</span>
                </div>
              </div>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Select State"
                    isDisabled={true}
                    styles={{
                      control: (base) => ({
                        ...base, minHeight: "55px", borderRadius: "8px", backgroundColor: "#F4F4F4", border: "none", boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
            </div>

            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-14 m-0 fw-bold text-muted">City</label>
                <div className="d-flex align-items-center">
                  <input type="checkbox" className="me-2" style={{ width: "16px", height: "16px", accentColor: "#b863f8" }} disabled />
                  <span className="fs-13 fw-bold text-muted">Select All</span>
                </div>
              </div>
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Select City"
                    isDisabled={true}
                    styles={{
                      control: (base) => ({
                        ...base, minHeight: "55px", borderRadius: "8px", backgroundColor: "#F4F4F4", border: "none", boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-14 m-0 fw-bold text-muted">Status</label>
                <div className="d-flex align-items-center">
                  <input type="checkbox" className="me-2" style={{ width: "16px", height: "16px", accentColor: "#b863f8" }} disabled />
                  <span className="fs-13 fw-bold text-muted">Select All</span>
                </div>
              </div>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Select Status"
                    isDisabled={true}
                    styles={{
                      control: (base) => ({
                        ...base, minHeight: "55px", borderRadius: "8px", backgroundColor: "#F4F4F4", border: "none", boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
            </div>

            <div className="col-md-6">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="f2 fs-14 m-0 fw-bold text-muted">Service type</label>
                <div className="d-flex align-items-center">
                  <input type="checkbox" className="me-2" style={{ width: "16px", height: "16px", accentColor: "#b863f8" }} disabled />
                  <span className="fs-13 fw-bold text-muted">Select All</span>
                </div>
              </div>
              <Controller
                name="service"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    isMulti
                    placeholder="Select Service Type"
                    isDisabled={true}
                    styles={{
                      control: (base) => ({
                        ...base, minHeight: "55px", borderRadius: "8px", backgroundColor: "#F4F4F4", border: "none", boxShadow: "none",
                      })
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                From Date
              </label>
              <div className="position-relative">
                <Controller
                  name="fromDate"
                  control={control}
                  rules={{
                    required: template?.notification_type > 0 ? "From Date is required" : false,
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className="form-control ps-3 w-100"
                      style={{ backgroundColor: "#F4F4F4", borderRadius: "8px", height: "55px", width: "100%", minWidth: "100%", border: errors.fromDate ? "1px solid red" : "none", outline: "none", display: "block" }}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      max={toDate ? moment(toDate).format("YYYY-MM-DD") : undefined}
                      disabled={isViewMode}
                    />
                  )}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                To Date
              </label>
              <div className="position-relative">
                <Controller
                  name="toDate"
                  control={control}
                  rules={{
                    required: template?.notification_type > 0 ? "To Date is required" : false,
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="date"
                      className="form-control ps-3 w-100"
                      style={{ backgroundColor: "#F4F4F4", borderRadius: "8px", height: "55px", width: "100%", minWidth: "100%", border: errors.toDate ? "1px solid red" : "none", outline: "none", display: "block" }}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                      }}
                      min={fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined}
                      disabled={isViewMode}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          
          <div className="row mb-4">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                User Count
              </label>
              <div className="w-100 d-block">
                <Controller
                  name="userCount"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      className="form-control w-100"
                      style={{ 
                        backgroundColor: "#F4F4F4", 
                        borderRadius: "8px", 
                        height: "55px", 
                        width: "100%", 
                        minWidth: "100%", 
                        padding: "0 16px",
                        border: "none", 
                        outline: "none", 
                        fontWeight: "600",
                        display: "block" 
                      }}
                      disabled={true}
                    />
                  )}
                />
              </div>
            </div>

            <div className="col-md-6">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                User list <span className="text-danger">*</span>
              </label>
              <Controller
                name="userList"
                control={control}
                rules={{
                  required: "User List is required",
                  validate: (value) =>
                    (Array.isArray(value) && value.length > 0) ||
                    "Please select at least one user",
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={
                      totalUserCount > 0
                        ? [
                            { value: "all", label: `All (${totalUserCount})` },
                            ...selectedUserOptions,
                          ]
                        : selectedUserOptions
                    }
                    inputValue={searchTerm}
                    onChange={(value, actionMeta) => {
                      const { action, option, removedValue } = actionMeta;

                      if (
                        action === "select-option" &&
                        option?.value === "all" &&
                        isServiceAllSelected
                      ) {
                        toast.warning(
                          "Cannot select 'All' users when Status is set to 'All'",
                        );
                        return;
                      }

                      if (
                        action === "select-option" &&
                        option?.value === "all"
                      ) {
                        setIsAllUsersSelected(true);
                        setExcludedUserIds([]);
                        field.onChange([
                          {
                            value: "all",
                            label: `All (${totalUserCount})`,
                          },
                        ]);
                        return;
                      }

                      if (
                        action === "clear" ||
                        (action === "remove-value" &&
                          removedValue?.value === "all") ||
                        !value ||
                        value.length === 0
                      ) {
                        setIsAllUsersSelected(false);
                        setExcludedUserIds([]);
                        field.onChange([]);
                        return;
                      }

                      if (isAllUsersSelected) {
                        const removedItem =
                          removedValue ||
                          (action === "deselect-option" ? option : null);
                        if (removedItem?.value === "all") {
                          setIsAllUsersSelected(false);
                          setExcludedUserIds([]);
                          field.onChange([]);
                          return;
                        }

                        if (action === "select-option" && option) {
                          const clickedId = String(option.value);
                          let newExcluded = [...excludedUserIds].map(
                            (id) => String(id),
                          );

                          if (newExcluded.includes(clickedId)) {
                            newExcluded = newExcluded.filter(
                              (id) => id !== clickedId,
                            );
                          } else {
                            newExcluded.push(clickedId);
                          }

                          if (
                            newExcluded.length === totalUserCount &&
                            totalUserCount > 0
                          ) {
                            setIsAllUsersSelected(false);
                            setExcludedUserIds([]);
                            field.onChange([]);
                            return;
                          }

                          setExcludedUserIds(newExcluded);
                          const currentCount =
                            totalUserCount - newExcluded.length;
                          const newLabel =
                            newExcluded.length === 0
                              ? `All (${totalUserCount})`
                              : `Selected (${currentCount})`;
                          field.onChange([
                            { value: "all", label: newLabel },
                          ]);
                        }
                      } else {
                        setIsAllUsersSelected(false);
                        setExcludedUserIds([]);
                        field.onChange(value);
                      }
                      setSearchTerm("");
                      fetchUserList(null, "");
                    }}
                    onInputChange={handleUserSearch}
                    isLoadingUsers={isLoadingUsers}
                    onMenuScrollToBottom={() => {
                      if (nextPageUrl && !isFetchingRef.current) {
                        fetchUserList(nextPageUrl, searchTerm);
                      }
                    }}
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isAllUsersSelected={isAllUsersSelected}
                    excludedUserIds={excludedUserIds}
                    placeholder="Select User"
                    isMulti
                    isClearable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    isDisabled={isViewMode}
                    styles={{
                      ...hideMultiValue,
                      control: (provided, state) => ({
                        ...hideMultiValue.control(provided, state),
                        border: errors.userList ? "1px solid red" : "none",
                        opacity: isViewMode ? 0.7 : 1,
                      }),
                      valueContainer: (provided) => ({
                        ...provided,
                        maxHeight: "140px",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": { width: "6px" },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#c1c1c1",
                          borderRadius: "8px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#a8a8a8",
                        },
                      }),
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
              Title <span className="text-danger">*</span>
            </label>
            <div className="w-100 d-block">
              <Controller
                name="title"
                control={control}
                rules={{ required: "Title is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    placeholder="Title"
                    className="form-control w-100"
                    style={{ 
                      backgroundColor: "#F4F4F4", 
                      borderRadius: "8px", 
                      height: "55px", 
                      width: "100%", 
                      minWidth: "100%", 
                      padding: "0 16px",
                      border: errors.title ? "1px solid red" : "none", 
                      outline: "none", 
                      fontWeight: "500",
                      display: "block" 
                    }}
                    disabled={isViewMode}
                  />
                )}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
              Description <span className="text-danger">*</span>
            </label>
            <div className="w-100 d-block">
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field }) => (
                  <textarea
                    {...field}
                    placeholder="text.."
                    rows="5"
                    className="form-control p-3 w-100"
                    style={{ 
                      backgroundColor: "#F4F4F4", 
                      borderRadius: "8px", 
                      width: "100%", 
                      minWidth: "100%", 
                      border: errors.description ? "1px solid red" : "none", 
                      outline: "none", 
                      resize: "none",
                      display: "block" 
                    }}
                    disabled={isViewMode}
                  />
                )}
              />
            </div>
          </div>

          <div className="row mb-5">
            <div className="col-md-6 mb-3 mb-md-0">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                Notification Send Date <span className="text-danger">*</span>
              </label>
              <Controller
                name="sendDate"
                control={control}
                rules={{ required: "Notification Send Date is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    className="form-control ps-3 w-100"
                    min={moment().format("YYYY-MM-DD")}
                    style={{ backgroundColor: "#F4F4F4", borderRadius: "8px", height: "55px", width: "100%", minWidth: "100%", border: errors.sendDate ? "1px solid red" : "none", outline: "none", display: "block" }}
                    disabled={isViewMode}
                  />
                )}
              />
            </div>

            <div className="col-md-6">
              <label className="f2 fs-14 mb-2 d-block fw-bold text-dark">
                Notification Send Time <span className="text-danger">*</span>
              </label>
              <Controller
                name="sendTime"
                control={control}
                rules={{ required: "Notification Send Time is required" }}
                render={({ field }) => (
                  <input
                    {...field}
                    type="time"
                    className="form-control ps-3 w-100"
                    style={{ backgroundColor: "#F4F4F4", borderRadius: "8px", height: "55px", width: "100%", minWidth: "100%", border: errors.sendTime ? "1px solid red" : "none", outline: "none", display: "block" }}
                    disabled={isViewMode}
                  />
                )}
              />
            </div>
          </div>

          <div className="d-flex justify-content-between mt-5 pt-2">
            <button
              type="button"
              className="btn px-5 py-3 fw-bold"
              style={{
                borderRadius: "12px",
                backgroundColor: "#f5e6ff",
                color: "#b863f8",
                border: "1px solid #e1c5f9",
                minWidth: "200px",
                fontSize: "16px",
              }}
              onClick={() => navigate(-1)}
            >
              {isViewMode ? "Back" : "Cancel"}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="btn px-5 py-3 text-white fw-bold"
                style={{
                  borderRadius: "12px",
                  backgroundColor: "#b863f8",
                  minWidth: "200px",
                  fontSize: "16px",
                  border: "none",
                }}
              >
                {isEditMode ? "Edit" : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SendTemplateNotification;
